import { Base } from '@studiometa/js-toolkit';

/**
 * @typedef {object} EventsListRef
 * @property {HTMLElement[]} events[]
 * @property {HTMLElement[]} eventsHead[]
 * @property {HTMLElement[]} eventsImage[]
 */

/**
 * @typedef {object} EventsListInterface
 * @property {EventsListRef} $refs
 */

/**
 * Homepage class.
 */
export default class EventsList extends Base {
  /**
   * Class config
   * @type {object}
   */
  static config = {
    name: 'EventsList',
    refs: ['events[]', 'eventsHead[]', 'eventsImage[]'],
  };

  /**
   * @type {number[]}
   */
  transforms = [];

  /**
   * @type {number}
   */
  currentIndexValue = 0;

  /**
   * Mounted Hook
   * @this {EventsList & EventsListInterface}
   */
  mounted() {
    this.setTransformsAndSetSectionHeight();
    this.currentIndex = 0;
  }

  /**
   * Set current index and render them
   * @this {EventsList & EventsListInterface}
   * @param {number} index
   */
  set currentIndex(index) {
    this.currentIndexValue = index;
    this.renderItems();
  }

  /**
   * Get current index
   * @this {EventsList & EventsListInterface}
   * @returns {number}
   */
  get currentIndex() {
    return this.currentIndexValue;
  }

  /**
   * Mounted Hook
   * @this {EventsList & EventsListInterface}
   */
  resized() {
    this.setTransformsAndSetSectionHeight();
    this.renderItems();
  }

  /**
   * Destroyed Hook
   * @this {EventsList & EventsListInterface}
   */
  destroyed() {
    this.$el.style.height = ''; // Reset the height of the component's element
  }

  /**
   * Set the transforms for the events and set the container height
   * @this {EventsList & EventsListInterface}
   */
  setTransformsAndSetSectionHeight() {
    const dimensions = this.$refs.events.map((el, index) => ({
      clientHeight: el.clientHeight,
      headClientHeight: this.$refs.eventsHead[index].clientHeight,
      padding: Number.parseInt(window.getComputedStyle(el).paddingTop, 10),
    }));

    let cumulatedHeight = 0;
    let cumulatedOffset = 0;

    cumulatedHeight += 0;
    cumulatedOffset += 0;

    dimensions.forEach((dimension, index) => {
      cumulatedHeight +=
        index === 0 ? dimension.clientHeight : dimension.headClientHeight + 2 * dimension.padding;

      this.transforms[index] = {
        idle: index === 0 ? 0 : -cumulatedOffset,
        hovered:
          index === 0
            ? 0
            : -cumulatedOffset -
              (dimension.clientHeight - (dimension.headClientHeight + 2 * dimension.padding)),
      };

      cumulatedOffset +=
        index === 0
          ? 0
          : dimension.clientHeight - dimension.headClientHeight - 2 * dimension.padding;
    });

    this.$el.style.height = `${cumulatedHeight}px`;
  }

  /**
   * Set styles and transforms according to the current index
   * @this {EventsList & EventsListInterface}
   */
  renderItems() {
    this.$refs.events.forEach((event, index) => {
      if (index <= this.currentIndex) {
        event.style.transform = `translateY(${this.transforms[index].hovered}px)`;
      } else {
        event.style.transform = `translateY(${this.transforms[index].idle}px)`;
      }

      if (index === this.currentIndex) {
        this.$refs.eventsImage[index].style.filter = '';
      } else {
        this.$refs.eventsImage[index].style.filter = 'saturate(0)';
      }
    });
  }

  /**
   * Set current index on hover
   * @param {MouseEvent} _
   * @param {number} hoveredEventIndex
   * @this {EventsList & EventsListInterface}
   */
  onEventsMouseenter(_, hoveredEventIndex) {
    this.currentIndex = hoveredEventIndex;
  }

  /**
   * Prevent navigation if the event is folded
   * @param {MouseEvent} event
   * @param {number} hoveredEventIndex
   */
  onEventsTouchstart(event, hoveredEventIndex) {
    if (this.currentIndex !== hoveredEventIndex) {
      event.preventDefault();
      this.currentIndex = hoveredEventIndex;
    }
  }
}
